/**
 * WorkOS invite trigger — shared by the Add User flow (step 2 of
 * `Workforce.handleAdd`) and the "Resend invite" row action
 * (WORKOS-INVITE-FLOW-PLAN Phase 4). Both fire the same SWA-co-located
 * `/api/invite-user` Function; extracting the call here keeps the two
 * paths' outcome copy identical instead of drifting apart.
 *
 * The dev/prod gating (skip the fetch entirely unless PROD or
 * VITE_ENABLE_LOCAL_INVITES) stays in the component — the two callers
 * want different copy for the disabled state, and import.meta.env
 * doesn't belong in a unit-testable module.
 */

/**
 * `status` tells a caller that shows per-row results (Workforce "Add
 * several") WHAT happened; `sub` stays the human sentence for toasts.
 *   sent      — invite email went out
 *   added     — existing WorkOS account, joined directly (no email)
 *   skipped   — already an org member (no email)
 *   error     — the Function or the network said no
 */
export type InviteStatus = 'sent' | 'added' | 'skipped' | 'error';
export type InviteOutcome = { ok: boolean; sub: string; status: InviteStatus };

/**
 * Map the `/api/invite-user` HTTP response onto the toast outcome.
 * Pure — the fetch wrapper below and the tests both go through this.
 *
 * 404/503 specifically mean the Function isn't reachable: surface a
 * clean "API not running" message rather than a scary HTTP code so a
 * dev forgetting `func start` doesn't read it as a code bug.
 */
export function parseInviteResponse(
  email: string,
  status: number,
  body: { status?: 'sent' | 'added' | 'skipped' | 'error'; reason?: string },
  dev: boolean,
): InviteOutcome {
  if (status === 404 || status === 503) {
    return {
      ok: false,
      status: 'error',
      sub: dev
        ? 'invite skipped (start `func start` in api/ to enable local invites)'
        : 'invite failed: API endpoint not reachable (deploy may be incomplete)',
    };
  }
  const httpOk = status >= 200 && status < 300;
  if (httpOk && body.status === 'sent') {
    return { ok: true, status: 'sent', sub: `invite emailed to ${email}` };
  }
  if (httpOk && body.status === 'added') {
    // Existing WorkOS account (e.g. an SSO user from another org): added as
    // a member directly, no invite email. They sign in the way they already do.
    return {
      ok: true,
      status: 'added',
      sub: `${email} already has an account: added directly, no invite email sent`,
    };
  }
  if (httpOk && body.status === 'skipped') {
    // WorkOS returned "user already in organization" — they're already
    // an org member (signed in via SSO at some point). No new invite
    // was sent (correct); their pending_user row will drain on their
    // next portal connection.
    return {
      ok: true,
      status: 'skipped',
      sub: `${email} is already a WorkOS member — will appear active on their next portal visit`,
    };
  }
  return { ok: false, status: 'error', sub: `invite failed: ${body.reason ?? `HTTP ${status}`}` };
}

/**
 * Fire the invite Function for one email. Never throws — network and
 * parse failures come back as a `{ ok: false }` outcome so callers can
 * toast them uniformly.
 */
export async function sendWorkosInvite(opts: {
  email: string;
  organizationId: string | null | undefined;
  /** WorkOS role slug; the Function defaults to `member`. */
  roleSlug?: string;
  getAccessToken: () => Promise<string>;
  dev: boolean;
}): Promise<InviteOutcome> {
  if (!opts.organizationId) {
    return { ok: false, status: 'error', sub: 'invite skipped (no org context)' };
  }
  try {
    const workosJwt = await opts.getAccessToken();
    // X-Workos-Authorization not Authorization: SWA's edge overwrites
    // the standard header with its own SCM JWT (verified 2026-06-15).
    const res = await fetch('/api/invite-user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Workos-Authorization': `Bearer ${workosJwt}`,
      },
      body: JSON.stringify({
        email: opts.email,
        organizationId: opts.organizationId,
        roleSlug: opts.roleSlug,
      }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      status?: 'sent' | 'added' | 'skipped' | 'error';
      reason?: string;
    };
    return parseInviteResponse(opts.email, res.status, body, opts.dev);
  } catch (err) {
    return {
      ok: false,
      status: 'error',
      sub: `invite call failed: ${err instanceof Error ? err.message : String(err)}`,
    };
  }
}
